/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import React, { useRef } from 'react';

import { useTabManagerContext } from '../context/TabManagerContext';
import { Tab } from '../helpers/codeEditorHelpers';
import { createVariablePositions } from '../helpers/codeEditorHelpers';
import { CachedTab } from '../TabManager';

import { Story, Step as UIStep } from '@/pages/projects/LiveAgentTypes';

// Refresh the editor 50ms after the last change to prevent too many re-renders
// while the user is typing
const HEAL_EDITOR_REFRESH_INTERVAL = 50;

export const useEditorIntegration = () => {
    const { editorRef, astParser, onSyntaxError, isDisabledRef, hidePlaywrightRef, variablesRef, queueRef, stepsRef } =
        useTabManagerContext();
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Helper to check if there's one running step or one in queue
    const hasOneRunningStepOrOneInQueue = () => {
        return stepsRef.current.some((step) => step.running) || (queueRef.current && queueRef.current.length > 0);
    };

    const clearAllDecorations = (
        activeTabIdRef: React.MutableRefObject<string>,
        tabsRef: React.MutableRefObject<Tab[]>,
    ) => {
        if (!editorRef.current) return;

        // Force clear ALL decorations from the editor
        const model = editorRef.current.getModel();
        if (model) {
            const allDecorations = model.getAllDecorations();
            const decorationIds = allDecorations.map((d) => d.id);
            editorRef.current.deltaDecorations(decorationIds, []);
        }

        // Clear current tab's decorations tracking
        const currentTab = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current);
        if (currentTab?.decorationManager) {
            currentTab.decorationManager.clearDecorations();
        }
    };

    const applyDecorationsForTab = (tab: Tab, code: string, init: boolean, tabSteps: UIStep[], blocksData: Story[]) => {
        if (!editorRef.current || !tab.decorationManager || !tab.metadataManager) return;

        // Parse code directly - AstParser now handles block functions automatically
        const functionCalls = astParser.parseCode(code);

        const variablePositions = createVariablePositions(code, variablesRef.current, astParser);

        if (init) {
            tab.metadataManager.updateFromAst(functionCalls, tabSteps, blocksData, {}, init, tab.decorationManager);
        }

        tab.decorationManager.createDecorations(
            tab.metadataManager.getMetadata(),
            isDisabledRef.current,
            hasOneRunningStepOrOneInQueue(),
            hidePlaywrightRef.current,
            variablePositions,
            tab.isMainTab ?? false, // Only show execution glyph icons for main tab
        );

        // Make sure the metadata is updated with the current step statuses if we were on another tab
        if (tab.id === 'main') {
            for (let i = 0; i < stepsRef.current.length; ++i) {
                tab.metadataManager.updateStatus(stepsRef.current[i], queueRef.current.includes(i));
            }
        }
    };

    const refreshMainTabMetadata = (tabsRef: React.MutableRefObject<Tab[]>) => {
        const mainTab = tabsRef.current.find((tab) => tab.id === 'main');
        if (!mainTab?.metadataManager) return;

        // Update metadata with current step statuses before refreshing decorations
        // This ensures the glyph icons reflect the correct execution states (PASS, FAIL, etc.)
        let updated = false;
        stepsRef.current.forEach((step, index) => {
            if (mainTab.metadataManager!.getMetadataById(step.id)) {
                updated = mainTab.metadataManager!.updateStatus(step, queueRef.current.includes(index)) || updated;
            }
        });

        if (updated) {
            // eslint-disable-next-line no-console
            console.log(`[TAB_MODIFICATION] Setting mainTab.isModified = true (refreshMainTabMetadata)`, {
                tabId: mainTab.id,
                reason: 'metadata updated with step statuses',
                location: 'useEditorIntegration.refreshMainTabMetadata',
            });
            mainTab.isModified = true;
        }
    };

    // === REFRESH DECORATIONS WHEN PROPS CHANGE ===
    // Function to refresh decorations for the current active tab
    // This is crucial for showing/hiding glyph icons when session status changes
    const refreshCurrentTabDecorations = (
        activeTabIdRef: React.MutableRefObject<string>,
        tabsRef: React.MutableRefObject<Tab[]>,
    ) => {
        if (!editorRef.current) return;

        const code = editorRef.current.getValue();
        const activeTab = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current);

        if (!activeTab?.metadataManager || !activeTab?.decorationManager) return;

        // Update metadata with current step statuses before refreshing decorations
        // This ensures the glyph icons reflect the correct execution states (PASS, FAIL, etc.)
        let hasUpdates = false;
        stepsRef.current.forEach((step, index) => {
            if (activeTab.metadataManager!.getMetadataById(step.id)) {
                hasUpdates =
                    activeTab.metadataManager!.updateStatus(step, queueRef.current.includes(index)) || hasUpdates;
            }
        });

        const variablePositions = createVariablePositions(code, variablesRef.current, astParser);

        // Update the decorations with current state
        activeTab.decorationManager.clearDecorations();
        activeTab.decorationManager.createDecorations(
            activeTab.metadataManager.getMetadata(),
            isDisabledRef.current,
            hasOneRunningStepOrOneInQueue(),
            hidePlaywrightRef.current,
            variablePositions,
            activeTab.isMainTab ?? false, // Only show execution glyph icons for main tab
        );

        if (hasUpdates) {
            // eslint-disable-next-line no-console
            console.log(`[TAB_MODIFICATION] Setting activeTab.isModified = true (refreshCurrentTabDecorations)`, {
                tabId: activeTab.id,
                reason: 'metadata updated with step statuses',
                location: 'useEditorIntegration.refreshCurrentTabDecorations',
            });
            activeTab.isModified = true;
        }
    };

    const applyTabContent = (
        tabId: string,
        tabSteps: UIStep[],
        tabContentRef: React.MutableRefObject<Map<string, string>>,
        tabsRef: React.MutableRefObject<Tab[]>,
        blocksData: Story[],
    ) => {
        if (!editorRef.current) {
            return;
        }

        // Get new content
        const newContent = tabContentRef.current.get(tabId) || '';

        // Update editor content using setValue (works with default model)
        editorRef.current.setValue(newContent);

        // Apply decorations for the new tab
        const tab = tabsRef.current.find((t) => t.id === tabId);

        if (tab?.decorationManager && tab?.metadataManager) {
            // Apply decorations using context values
            applyDecorationsForTab(
                tab,
                newContent,
                false, // init = false since this is a tab switch
                tabSteps,
                blocksData,
            );
        }
    };

    const handleEditorChange = (
        activeTabIdRef: React.MutableRefObject<string>,
        tabsRef: React.MutableRefObject<Tab[]>,
        blocksData: Story[],
        blockCacheRef: React.MutableRefObject<Map<string, CachedTab>>,
        updateCurrentTabModificationStatus: () => void,
    ) => {
        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set a new timeout to debounce changes
        timeoutRef.current = setTimeout(() => {
            try {
                const code = editorRef.current?.getValue() ?? '';
                const activeTab = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current);

                if (!activeTab?.metadataManager || !activeTab?.decorationManager) return;

                // Check if current tab is modified
                updateCurrentTabModificationStatus();

                // Parse code directly - AstParser now handles block functions automatically
                const functionCalls = astParser.parseCode(code);

                // Make sure the syntax is correct before parsing
                const { isValid, errors } = astParser.validateSyntax(code);

                // Update syntax error state
                onSyntaxError?.(isValid === false, errors);

                if (!isValid) {
                    return;
                }

                const variablePositions = createVariablePositions(code, variablesRef.current, astParser);

                // Grab the steps for block tabs
                const blockTabsSteps: Record<string, UIStep[]> = {};
                for (const [key, tab] of blockCacheRef.current) {
                    if (key !== 'main') {
                        const blockSteps = tab.steps;
                        if (blockSteps) {
                            blockTabsSteps[key] = blockSteps;
                        }
                    }
                }

                // Update the metadata
                activeTab.metadataManager.updateFromAst(
                    functionCalls,
                    [],
                    blocksData,
                    blockTabsSteps,
                    false,
                    activeTab.decorationManager,
                );

                // Update the decorations
                activeTab.decorationManager.clearDecorations();
                activeTab.decorationManager.createDecorations(
                    activeTab.metadataManager.getMetadata(),
                    isDisabledRef.current,
                    hasOneRunningStepOrOneInQueue(),
                    hidePlaywrightRef.current,
                    variablePositions,
                    activeTab.isMainTab ?? false, // Only show execution glyph icons for main tab
                );
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error in handleEditorChange:', error);
            }
        }, HEAL_EDITOR_REFRESH_INTERVAL);
    };

    return {
        applyDecorationsForTab,
        clearAllDecorations,
        applyTabContent,
        handleEditorChange,
        refreshMainTabMetadata,
        refreshCurrentTabDecorations,
    };
};
